import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  BadgeCheck,
  ChevronDown,
  Clock3,
  CreditCard,
  Info,
  ShieldCheck,
  Sparkles,
  TrainFront,
} from "lucide-react";
import { trainsData } from "../Train";
// import TrainLoader from "../../../Loader/Train/Trainloader.jsx";
import Login from "../../../Main/Login/Login";
import Signup from "../../../Main/Signup/Signup";

import "./Trainbooking.css";

const fallbackTrain = {
  id: "default",
  name: "Shram Shkti Exp",
  number: "12452",
  departure: "11:55 PM",
  departureCity: "New Delhi",
  arrival: "6:20 AM",
  arrivalCity: "Kanpur Central",
  duration: "6h 25 mins",
  classes: [{ code: "SL", price: 265, available: 27 }],
  runsOn: ["S", "M", "T", "W", "T", "F", "S"],
};

const stationCode = (city) => {
  if (/delhi/i.test(city)) return "NDLS";
  if (/kanpur/i.test(city)) return "CNB";
  return city
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();
};

const formatTime = (value) => {
  if (!value) return "";
  if (/am|pm/i.test(value)) return value;
  const [hourText, minuteText = "00"] = String(value).split(":");
  const hour = Number(hourText);
  if (Number.isNaN(hour)) return value;
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minuteText} ${period}`;
};

const safeDate = (dateText) => {
  const parsed = new Date(`${dateText || ""}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? new Date("2026-05-07T00:00:00") : parsed;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatJourneyDate = (date) =>
  date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

const availabilityText = (cls) => {
  if (cls.available !== null && cls.available !== undefined) {
    return `AVAILABLE-${String(cls.available).padStart(4, "0")}`;
  }
  return cls.waitlist || "CHECK STATUS";
};



export default function Trainbooking() {
  const navigate = useNavigate();

  const { trainId } = useParams();
  const [isTrainLoading, setIsTrainLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const [refundPlan, setRefundPlan] = useState("full");
  const [travellers, setTravellers] = useState([
    { id: 1, name: "disha", gender: "F", age: 20, berth: "Lower", selected: false },
  ]);
  const [editingTravellerId, setEditingTravellerId] = useState(null);
  const [travellerForm, setTravellerForm] = useState({
    name: "",
    gender: "F",
    age: "",
    berth: "Lower",
  });
  const [irctcUsername, setIrctcUsername] = useState("");
  const [contactInfo, setContactInfo] = useState({ email: "", mobile: "" });
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [preferencesOpen, setPreferencesOpen] = useState(true);
  const [billingState, setBillingState] = useState("Delhi");
  const [saveBilling, setSaveBilling] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add("train-booking-page", "feature-navbar-visible");
    document.body.classList.remove("feature-navbar-hidden");
    window.scrollTo({ top: 0, behavior: "auto" });

    return () => {
      document.body.classList.remove("train-booking-page");
    };
  }, []);

  const train = useMemo(
    () => trainsData.find((item) => String(item.id) === String(trainId)) || fallbackTrain,
    [trainId]
  );

  useEffect(() => {
    // Simulated loading while page initializes
    setIsTrainLoading(true);
    const t = setTimeout(() => setIsTrainLoading(false), 900);
    return () => clearTimeout(t);
  }, [trainId]);


  const selectedClassCode = searchParams.get("class") || train.classes?.[0]?.code || "SL";
  const selectedClass =
    train.classes?.find((item) => item.code === selectedClassCode) || train.classes?.[0] || fallbackTrain.classes[0];

  const travelDate = safeDate(searchParams.get("date"));
  const arrivalDate = train.arrivalTime24 < train.departureTime24 ? addDays(travelDate, 1) : travelDate;
  const departureLabel = formatJourneyDate(travelDate);
  const arrivalLabel = formatJourneyDate(arrivalDate);
  const departureTime = formatTime(train.departure);
  const arrivalTime = formatTime(train.arrival);
  const baseFare = Number(selectedClass.price || 265);
  const reservationCharge = selectedClass.code === "SL" || selectedClass.code === "2S" ? 20 : 40;
  const superfastCharge = train.typeCode === "SF" || train.tags?.includes("fast") ? 30 : 0;
  const tatkalFare = selectedClass.isTatkal ? Math.max(100, Math.round(baseFare * 0.18)) : 0;
  const refundCharge = 103;
  const total = baseFare + reservationCharge + superfastCharge + tatkalFare;
  const fromCode = stationCode(train.departureCity);
  const toCode = stationCode(train.arrivalCity);
  const boardingStations = [
    `${train.departureCity.toUpperCase()} (${fromCode}) - ${departureTime} (${departureLabel})`,
    `ANAND VIHAR TERMINAL (${fromCode}) - ${departureTime} (${departureLabel})`,
    `KANPUR CENTRAL (${toCode}) - ${arrivalTime} (${arrivalLabel})`,
  ];

  const changeClass = (code) => {
    const next = new URLSearchParams(searchParams);
    next.set("class", code);
    setSearchParams(next);
  };

  const startAddTraveller = () => {
    setEditingTravellerId("new");
    setTravellerForm({ name: "", gender: "F", age: "", berth: "Lower" });
  };

  const startEditTraveller = (traveller) => {
    setEditingTravellerId(traveller.id);
    setTravellerForm({
      name: traveller.name,
      gender: traveller.gender,
      age: String(traveller.age),
      berth: traveller.berth,
    });
  };

  const saveTraveller = () => {
    const cleanedName = travellerForm.name.trim() || "Traveller";
    const cleanedAge = Math.max(1, Number(travellerForm.age) || 18);
    if (editingTravellerId === "new") {
      setTravellers((current) => [
        ...current,
        {
          id: Date.now(),
          name: cleanedName,
          gender: travellerForm.gender,
          age: cleanedAge,
          berth: travellerForm.berth,
          selected: true,
        },
      ]);
    } else {
      setTravellers((current) =>
        current.map((traveller) =>
          traveller.id === editingTravellerId
            ? {
                ...traveller,
                name: cleanedName,
                gender: travellerForm.gender,
                age: cleanedAge,
                berth: travellerForm.berth,
              }
            : traveller
        )
      );
    }
    setEditingTravellerId(null);
  };

  const toggleTraveller = (id) => {
    setTravellers((current) =>
      current.map((traveller) =>
        traveller.id === id ? { ...traveller, selected: !traveller.selected } : traveller
      )
    );
  };

  // if (isTrainLoading) {
  //   return <TrainLoader />;
  // }

  return (
    <main className="tb-page">

      <section className="tb-shell">
        <button className="tb-back" type="button" onClick={() => navigate("/trains")}>
          Back to trains
        </button>

        <div className="tb-layout">
          <div className="tb-main">
            <section className="tb-journey-card">
              <div className="tb-train-title">
                <h1>{train.name}</h1>
                <p>
                  #{train.number}
                  <span />
                  Departs on:
                  <b>{(train.runsOn || fallbackTrain.runsOn).join(" ")}</b>
                </p>
              </div>

              <div className="tb-route">
                <div className="tb-time-block">
                  <strong>{departureTime}</strong>
                  <span>{departureLabel}</span>
                  <p>{train.departureCity} ({fromCode})</p>
                </div>
                <div className="tb-duration">
                  <i />
                  <span>{train.duration}</span>
                  <i />
                </div>
                <div className="tb-time-block">
                  <strong>{arrivalTime}</strong>
                  <span>{arrivalLabel}</span>
                  <p>{train.arrivalCity} ({toCode})</p>
                </div>
              </div>
            </section>

            <section className="tb-selection-grid">
              <div>
                <h2>Availability Status</h2>
                <div className="tb-status-card">
                  <strong>{selectedClass.code}</strong>
                  <div>
                    <b>
                      {availabilityText(selectedClass)}
                    </b>
                    <span>Updated {train.updatedAgo || "few mins ago"}</span>
                  </div>
                </div>
                <div className="tb-class-picker">
                  {(train.classes || []).map((cls, index) => (
                    <button
                      key={`${cls.code}-${index}`}
                      className={cls.code === selectedClass.code ? "active" : ""}
                      type="button"
                      onClick={() => changeClass(cls.code)}
                    >
                      {cls.code}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h2>Your Boarding Station</h2>
                <select className="tb-select" defaultValue={boardingStations[0]}>
                  {boardingStations.map((station) => (
                    <option key={station} value={station}>
                      {station}
                    </option>
                  ))}
                </select>
                <button className="tb-change-link" type="button">
                  <em>Change</em>
                  <ChevronDown size={18} />
                </button>
              </div>
            </section>

            <section className="tb-refund-card">
              <div className="tb-refund-head">
                <ShieldCheck size={34} />
                <div>
                  <h2>Get full ticket fare refund on Cancellation</h2>
                  <p>Just at Rs {refundCharge} per person</p>
                </div>
              </div>

              <div className="tb-refund-options">
                <label className={refundPlan === "full" ? "active" : ""}>
                  <input
                    type="radio"
                    name="refund"
                    checked={refundPlan === "full"}
                    onChange={() => setRefundPlan("full")}
                  />
                  <span>
                    Zero charges when the ticket is cancelled
                    <b>
                      Refund: Rs {total.toLocaleString("en-IN")} per person <BadgeCheck size={14} />
                    </b>
                  </span>
                </label>
                <label className={refundPlan === "fees" ? "active" : ""}>
                  <input
                    type="radio"
                    name="refund"
                    checked={refundPlan === "fees"}
                    onChange={() => setRefundPlan("fees")}
                  />
                  <span>Pay fees on cancellation</span>
                </label>
              </div>

              <div className="tb-cancel-note">
                <Info size={15} />
                35% of our users cancel their ticket due to change in plans
              </div>
              <a href="#terms">Terms & Conditions</a>
            </section>

            <section className="tb-traveller-section">
              <h2>Add Travellers & Preferences</h2>
              <div className="tb-traveller-card">
                {travellers.map((traveller) => (
                  <div className="tb-traveller-row" key={traveller.id}>
                    <label className="tb-traveller-check">
                      <input
                        type="checkbox"
                        checked={traveller.selected}
                        onChange={() => toggleTraveller(traveller.id)}
                      />
                      <span>
                        <b>{traveller.name}&#123;{traveller.gender}&#125;, {traveller.age}</b>
                        <em>{traveller.berth}</em>
                      </span>
                    </label>
                    <button type="button" onClick={() => startEditTraveller(traveller)}>
                      EDIT
                    </button>
                  </div>
                ))}

                {editingTravellerId && (
                  <div className="tb-traveller-form">
                    <input
                      value={travellerForm.name}
                      onChange={(e) => setTravellerForm((current) => ({ ...current, name: e.target.value }))}
                      placeholder="Traveller name"
                    />
                    <select
                      value={travellerForm.gender}
                      onChange={(e) => setTravellerForm((current) => ({ ...current, gender: e.target.value }))}
                    >
                      <option value="F">Female</option>
                      <option value="M">Male</option>
                      <option value="O">Other</option>
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={travellerForm.age}
                      onChange={(e) => setTravellerForm((current) => ({ ...current, age: e.target.value }))}
                      placeholder="Age"
                    />
                    <select
                      value={travellerForm.berth}
                      onChange={(e) => setTravellerForm((current) => ({ ...current, berth: e.target.value }))}
                    >
                      <option>Lower</option>
                      <option>Middle</option>
                      <option>Upper</option>
                      <option>Side Lower</option>
                      <option>Side Upper</option>
                      <option>No Preference</option>
                    </select>
                    <div className="tb-traveller-actions">
                      <button type="button" onClick={() => setEditingTravellerId(null)}>
                        Cancel
                      </button>
                      <button type="button" onClick={saveTraveller}>
                        Save Traveller
                      </button>
                    </div>
                  </div>
                )}

                <button className="tb-add-traveller" type="button" onClick={startAddTraveller}>
                  <span>+</span>
                  ADD TRAVELLER
                </button>
              </div>
            </section>

            <section className="tb-irctc-section">
              <h2>IRCTC Account Details</h2>
              <div className="tb-irctc-card">
                <div className="tb-irctc-label">
                  <strong>IRCTC Username</strong>
                  <button type="button">Why is this required?</button>
                </div>
                <p>IRCTC username will be required after Payment. Please ensure you have entered correct username.</p>
                <div className="tb-irctc-input-row">
                  <input
                    value={irctcUsername}
                    onChange={(e) => setIrctcUsername(e.target.value)}
                    placeholder="Enter IRCTC username"
                  />
                  <button type="button">CHANGE <span>→</span></button>
                </div>

                <div className="tb-irctc-password">
                  <strong>IRCTC Password</strong>
                  <span>
                    Please ensure you have your password ready. In case you don't have your password, we strongly recommend that you get a new password now.
                  </span>
                  <button type="button">Get New Password</button>
                </div>
              </div>
            </section>

            <section className="tb-contact-section">
              <h2>
                Contact Information
                <span>(We'll send your ticket and status updates here)</span>
              </h2>
              <div className="tb-contact-grid">
                <label>
                  Email Id
                  <input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) =>
                      setContactInfo((current) => ({ ...current, email: e.target.value }))
                    }
                    placeholder="Enter Email Id"
                  />
                </label>
                <label>
                  Mobile Number
                  <input
                    type="tel"
                    value={contactInfo.mobile}
                    onChange={(e) =>
                      setContactInfo((current) => ({ ...current, mobile: e.target.value }))
                    }
                    placeholder="Enter Mobile Number"
                  />
                </label>
              </div>
            </section>

            <section className="tb-offers-section">
              <h2>Offers & Discounts</h2>
              <div className="tb-coupon-card">
                <label className="tb-coupon-toggle">
                  <input
                    type="radio"
                    checked={couponOpen}
                    onChange={() => setCouponOpen(true)}
                  />
                  <span>HAVE A COUPON CODE?</span>
                </label>
                {couponOpen && (
                  <input
                    className="tb-coupon-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                  />
                )}
                <button type="button">APPLY</button>
              </div>
            </section>

            <section className="tb-preferences-section">
              <button
                className="tb-preferences-title"
                type="button"
                onClick={() => setPreferencesOpen((current) => !current)}
              >
                <strong>Additional Preferences</strong>
                <span>Optional</span>
                <em>{preferencesOpen ? "⌃" : "⌄"}</em>
              </button>

              {preferencesOpen && (
                <div className="tb-preferences-card">
                  <div className="tb-state-copy">
                    <strong>Your State</strong>
                    <p>
                      (Required for GST purpose on your tax invoice. You can edit this anytime later in your profile section.)
                    </p>
                  </div>
                  <label className="tb-state-select">
                    Select the State
                    <select value={billingState} onChange={(e) => setBillingState(e.target.value)}>
                      {["Delhi", "Uttar Pradesh", "Haryana", "Maharashtra", "Rajasthan", "Karnataka", "Tamil Nadu"].map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="tb-save-billing">
                    <input
                      type="checkbox"
                      checked={saveBilling}
                      onChange={(e) => setSaveBilling(e.target.checked)}
                    />
                    Confirm and save billing details to your profile
                  </label>
                </div>
              )}
            </section>

            <section className="tb-policy-section">
              <h2>Booking Policies</h2>
              <div className="tb-policy-card">
                {[
                  "Passenger details must match the valid government ID carried during travel.",
                  "Cancellation charges and refund timelines depend on railway rules and ticket status.",
                  "Boarding station changes are allowed only within the permitted IRCTC time window.",
                  "Tatkal and premium tatkal tickets may have restricted cancellation refunds.",
                ].map((policy) => (
                  <div className="tb-policy-item" key={policy}>
                    <span>i</span>
                    <p>{policy}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="tb-advisory-section">
              <h2>Travel Advisory</h2>
              <div className="tb-advisory-card">
                Please read health advisories by relevant authorities.
                <button type="button">KNOW MORE</button>
              </div>
              <p>
                By proceeding with the booking, I confirm that I have read and I accept the{" "}
                <a href="#cancellation">Cancellation & Refund Policy</a>,{" "}
                <a href="#privacy">Privacy Policy</a>,{" "}
                <a href="#user-agreement">User Agreement</a> and{" "}
                <a href="#terms">Terms of Service</a>
              </p>
            </section>
          </div>

          <aside className="tb-summary">
            <div className="tb-login-banner">
              <Sparkles size={22} />
              <p>Zero Convenience fees for logged-in users</p>
              <button type="button" onClick={() => setLoginOpen(true)}>Login</button>
            </div>

            <div className="tb-price-card">
              <div className="tb-pay-wrap">
                <button type="button">
                  <CreditCard size={18} />
                  PAY & BOOK NOW
                </button>
              </div>

              <div className="tb-fare-row">
                <span>Base fare per adult</span>
                <b>Rs {baseFare.toLocaleString("en-IN")}</b>
              </div>
              <div className="tb-fare-row">
                <span>Reservation charge</span>
                <b>Rs {reservationCharge}</b>
              </div>
              <div className="tb-fare-row">
                <span>Superfast charge</span>
                <b>Rs {superfastCharge}</b>
              </div>
              <div className="tb-fare-row">
                <span>Tatkal Fare</span>
                <b>Rs {tatkalFare}</b>
              </div>
              <div className="tb-fare-row">
                <span>Travel date</span>
                <b>{departureLabel}</b>
              </div>
              <div className="tb-total-row">
                <span>Total Price per adult</span>
                <b>Rs {total.toLocaleString("en-IN")}</b>
              </div>
            </div>

            <div className="tb-help-card">
              <TrainFront size={18} />
              <span>IRCTC authorised booking partner</span>
            </div>
          </aside>
        </div>
      </section>

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToSignup={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
        />
      )}

      {signupOpen && (
        <Signup
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
        />
      )}
    </main>
  );
}
